import { useEffect, useState } from 'react';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';

import type { TRaisersEdgeSyncSummaryResult } from './RaisersEdgeSyncSummary.utils';

/**
 * Loads the summary bar values once the integration is active, and again after each save, so the
 * attention count follows mapping edits. `undefined` until the first load settles; `null` if the
 * request failed. A reload keeps the last values on screen until it settles.
 */
export function useRaisersEdgeSyncSummary(
  integration: BlackbaudRaisersEdgeIntegration | undefined,
  isSaving: boolean,
): TRaisersEdgeSyncSummaryResult | undefined {
  const [summary, setSummary] = useState<TRaisersEdgeSyncSummaryResult>();
  const isActive = !!integration?.isActive;

  // `summary` is left out on purpose: the effect sets it.
  useEffect(() => {
    if (!integration || !isActive || isSaving) {
      return;
    }
    // Ignore a response that lands after unmount, a newer load, or a save that started.
    let isCurrent = true;

    async function load(model: BlackbaudRaisersEdgeIntegration): Promise<void> {
      try {
        const loaded = await model.getSyncSummary();
        if (isCurrent) {
          setSummary(loaded);
        }
      } catch {
        // The bar shows each value as unavailable; the rest of the page still works.
        if (isCurrent) {
          setSummary(null);
        }
      }
    }

    void load(integration);
    return (): void => {
      isCurrent = false;
    };
  }, [integration?.id, isActive, isSaving]);

  return summary;
}
